import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Circle,
  Square,
  Download,
  RotateCcw,
  AlertTriangle,
  Trash2,
  Loader,
  Sparkles,
  Target,
  Upload,
  FileVideo,
  CheckCircle2,
  X as XIcon,
  ChevronLeft,
} from 'lucide-react';
import { EXERCISE_LIST, getExercise } from '../../modules/biomechanics/exercises.js';
import { evaluate, evaluateFrame } from '../../modules/biomechanics/evaluator.js';
import { processVideo, compareSeries, computeMetrics } from '../../modules/biomechanics/executionAnalysis.js';

/**
 * PoseCamera
 * ---------------------------------------------------------
 * Analiza la postura en tiempo real con MediaPipe Pose Landmarker
 * 100% en el dispositivo (WebAssembly + WebGL). Nada se sube al servidor.
 *
 * El video grabado se DESCARGA al dispositivo del usuario.
 */

const WASM_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm';
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/1/pose_landmarker_full.task';

// Conexiones del esqueleto (33 landmarks MediaPipe Pose)
const POSE_CONNECTIONS = [
  [11, 12], [11, 13], [13, 15], [15, 17], [15, 19], [15, 21], [17, 19],
  [12, 14], [14, 16], [16, 18], [16, 20], [16, 22], [18, 20],
  [11, 23], [12, 24], [23, 24],
  [23, 25], [25, 27], [27, 29], [27, 31], [29, 31],
  [24, 26], [26, 28], [28, 30], [28, 32], [30, 32],
  [9, 10], [0, 4], [4, 5], [5, 6], [6, 8], [0, 1], [1, 2], [2, 3], [3, 7],
];

const pickMimeType = () => {
  if (typeof window === 'undefined' || !window.MediaRecorder) return '';
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ];
  return candidates.find(c => window.MediaRecorder.isTypeSupported(c)) || '';
};

const fmtTime = (ms) => {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m.toString().padStart(2, '0')}:${r.toString().padStart(2, '0')}`;
};

// ---- One Euro filter (smooths landmarks; adaptive so it doesn't lag on fast motion) ----
const alphaFor = (cutoff, dt) => {
  const tau = 1 / (2 * Math.PI * cutoff);
  return 1 / (1 + tau / dt);
};
function createOneEuro({ minCutoff = 1.2, beta = 0.012, dCutoff = 1.0 } = {}) {
  let xPrev = null, dxPrev = 0, tPrev = null;
  return (x, tMs) => {
    if (tPrev == null) { tPrev = tMs; xPrev = x; return x; }
    const dt = Math.max(1e-3, (tMs - tPrev) / 1000);
    const dx = (x - xPrev) / dt;
    const aD = alphaFor(dCutoff, dt);
    const edx = aD * dx + (1 - aD) * dxPrev;
    const cutoff = minCutoff + beta * Math.abs(edx);
    const aX = alphaFor(cutoff, dt);
    const y = aX * x + (1 - aX) * xPrev;
    xPrev = y; dxPrev = edx; tPrev = tMs;
    return y;
  };
}
function createLandmarkSmoother(n = 33) {
  const fx = Array.from({ length: n }, () => createOneEuro());
  const fy = Array.from({ length: n }, () => createOneEuro());
  const fz = Array.from({ length: n }, () => createOneEuro());
  return (landmarks, tMs) => landmarks.map((p, i) => ({
    x: fx[i](p.x, tMs),
    y: fy[i](p.y, tMs),
    z: fz[i](p.z ?? 0, tMs),
    visibility: p.visibility,
  }));
}

const PoseCamera = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const poseRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const lastVideoTimeRef = useRef(-1);
  const fpsRef = useRef({ frames: 0, last: performance.now() });
  const startTsRef = useRef(0);
  const timerRef = useRef(null);
  const smootherRef = useRef(createLandmarkSmoother(33));

  const [loading, setLoading] = useState(true);
  const [loadingMsg, setLoadingMsg] = useState('Cargando modelo de IA...');
  const [error, setError] = useState('');
  const [facingMode, setFacingMode] = useState('environment');
  const [isRecording, setIsRecording] = useState(false);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordedMime, setRecordedMime] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [fps, setFps] = useState(0);
  const [showLandmarks, setShowLandmarks] = useState(true);
  const [videoAspect, setVideoAspect] = useState(9 / 16);
  const [slowMoProcessing, setSlowMoProcessing] = useState(null); // null | 0.5 | 0.25
  const [slowMoProgress, setSlowMoProgress] = useState(0);

  // ---------- Análisis biomecánico ----------
  const landmarksBufferRef = useRef([]);       // [{ t, lm }]
  const isRecordingRef = useRef(false);
  const runAnalysisRef = useRef(() => {});
  const processingRefVideoRef = useRef(false); // pausa detección en loop principal
  const liveExerciseRef = useRef(null);         // ejercicio activo para overlay en tiempo real
  const recordedVideoRef = useRef(null);        // <video> del preview post-grabación

  const [mode, setMode] = useState(null);       // null | 'basic' | 'execution'
  const [selectedExerciseId, setSelectedExerciseId] = useState(EXERCISE_LIST[0]?.id || null);
  const [referenceVideoUrl, setReferenceVideoUrl] = useState(null);
  const [referenceData, setReferenceData] = useState(null);  // { duration, timeline, metrics }
  const [referenceProcessing, setReferenceProcessing] = useState(false);
  const [referenceProgress, setReferenceProgress] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);   // para modo 'basic'
  const [comparisonResult, setComparisonResult] = useState(null); // para modo 'execution'

  // Mantenemos `liveExerciseRef` sincronizado con el ejercicio seleccionado
  // para que renderFrame pueda usarlo sin regenerar el useCallback.
  useEffect(() => {
    liveExerciseRef.current = (mode === 'basic' && selectedExerciseId)
      ? getExercise(selectedExerciseId) : null;
  }, [mode, selectedExerciseId]);

  // --------------------- Init MediaPipe ---------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingMsg('Cargando MediaPipe...');
        const vision = await import('@mediapipe/tasks-vision');
        if (cancelled) return;
        setLoadingMsg('Descargando modelo (primera vez ~3MB)...');
        const fileset = await vision.FilesetResolver.forVisionTasks(WASM_URL);
        if (cancelled) return;
        const pose = await vision.PoseLandmarker.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
          runningMode: 'VIDEO',
          numPoses: 1,
          minPoseDetectionConfidence: 0.5,
          minPosePresenceConfidence: 0.5,
          minTrackingConfidence: 0.5,
        });
        if (cancelled) { pose.close(); return; }
        poseRef.current = pose;
      } catch (e) {
        console.error('MediaPipe init error:', e);
        if (!cancelled) setError('No se pudo cargar MediaPipe. Revisa tu conexión e intenta de nuevo.');
      }
    })();
    return () => {
      cancelled = true;
      if (poseRef.current) { try { poseRef.current.close(); } catch {} poseRef.current = null; }
    };
  }, []);

  // --------------------- Iniciar cámara ---------------------
  const startCamera = useCallback(async (mode) => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: mode },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      await new Promise((res) => {
        video.onloadedmetadata = () => { video.play(); res(); };
      });
      // Ajustar canvas al tamaño real del video
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
      }
      if (video.videoWidth && video.videoHeight) {
        setVideoAspect(video.videoWidth / video.videoHeight);
      }
      setLoading(false);
    } catch (e) {
      console.error('Camera error:', e);
      setError('No se pudo acceder a la cámara. Verifica los permisos.');
      setLoading(false);
    }
  }, []);

  // Trigger camera once MediaPipe is ready (initial mount)
  useEffect(() => {
    const id = setInterval(() => {
      if (poseRef.current && !streamRef.current) {
        startCamera(facingMode);
        clearInterval(id);
      }
    }, 200);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------------------- Render loop ---------------------
  const renderFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const pose = poseRef.current;
    if (!video || !canvas || !pose || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(renderFrame);
      return;
    }

    const ctx = canvas.getContext('2d');
    // Keep canvas size in sync
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }

    // Draw current frame
    ctx.save();
    if (facingMode === 'user') { ctx.translate(canvas.width, 0); ctx.scale(-1, 1); }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.restore();

    // Pose detection (pausada mientras procesamos el video de referencia)
    const now = performance.now();
    if (!processingRefVideoRef.current && video.currentTime !== lastVideoTimeRef.current) {
      lastVideoTimeRef.current = video.currentTime;
      try {
        const result = pose.detectForVideo(video, now);
        if (result && result.landmarks && result.landmarks.length > 0) {
          const smoothed = smootherRef.current(result.landmarks[0], now);
          // Capturar landmarks durante grabación para análisis biomecánico
          if (isRecordingRef.current) {
            landmarksBufferRef.current.push({
              t: now - startTsRef.current,
              lm: smoothed,
            });
          }
          // Overlay en tiempo real: evaluar el frame contra el ejercicio activo
          let liveWarnings = [];
          if (liveExerciseRef.current) {
            liveWarnings = evaluateFrame(liveExerciseRef.current, smoothed, 0.35);
          }
          if (showLandmarks) {
            drawPose(ctx, smoothed, canvas.width, canvas.height, facingMode === 'user', liveWarnings);
          }
          if (liveWarnings.length > 0) {
            drawWarningBadge(ctx, liveWarnings[0], canvas.width, canvas.height);
          }
        }
      } catch (e) { /* skip frame */ }
    }

    // FPS counter
    fpsRef.current.frames += 1;
    if (now - fpsRef.current.last > 1000) {
      setFps(Math.round((fpsRef.current.frames * 1000) / (now - fpsRef.current.last)));
      fpsRef.current.frames = 0;
      fpsRef.current.last = now;
    }

    rafRef.current = requestAnimationFrame(renderFrame);
  }, [facingMode, showLandmarks]);

  useEffect(() => {
    if (!loading) {
      rafRef.current = requestAnimationFrame(renderFrame);
    }
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [loading, renderFrame]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        try { recorderRef.current.stop(); } catch {}
      }
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------------------- Drawing helpers ---------------------
  function drawPose(ctx, landmarks, w, h, mirror, warnings = []) {
    ctx.save();
    if (mirror) { ctx.translate(w, 0); ctx.scale(-1, 1); }

    // Unificar landmarks problemáticos en un Set
    const warnSet = new Set();
    for (const w1 of warnings) {
      if (w1.landmarks) for (const i of w1.landmarks) warnSet.add(i);
    }

    const baseWidth = Math.max(2, w / 300);
    ctx.lineCap = 'round';

    // Huesos (connections): rojo si cualquiera de los dos extremos está en warnSet
    POSE_CONNECTIONS.forEach(([a, b]) => {
      const p1 = landmarks[a];
      const p2 = landmarks[b];
      if (!p1 || !p2) return;
      const flagged = warnSet.has(a) || warnSet.has(b);
      ctx.strokeStyle = flagged ? '#ef4444' : '#8b5cf6';
      ctx.lineWidth = flagged ? baseWidth * 1.5 : baseWidth;
      ctx.beginPath();
      ctx.moveTo(p1.x * w, p1.y * h);
      ctx.lineTo(p2.x * w, p2.y * h);
      ctx.stroke();
    });

    // Articulaciones
    const r = Math.max(3, w / 220);
    landmarks.forEach((pt, i) => {
      ctx.fillStyle = warnSet.has(i) ? '#ef4444' : '#fbbf24';
      ctx.beginPath();
      ctx.arc(pt.x * w, pt.y * h, warnSet.has(i) ? r * 1.3 : r, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  }

  // Badge de alerta en la parte superior del canvas
  function drawWarningBadge(ctx, warning, w, h) {
    const text = warning.check?.message || '';
    if (!text) return;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const fontSize = Math.max(14, Math.round(w / 35));
    ctx.font = `700 ${fontSize}px -apple-system, Segoe UI, Roboto, sans-serif`;
    const metrics = ctx.measureText(text);
    const padX = fontSize * 0.8;
    const padY = fontSize * 0.5;
    const boxW = Math.min(w * 0.92, metrics.width + padX * 2);
    const boxH = fontSize + padY * 2;
    const cx = w / 2;
    const cy = boxH / 2 + 12;

    // Fondo
    const severity = warning.severity || 0;
    const color = severity >= 0.7 ? 'rgba(220,38,38,0.92)' : 'rgba(217,119,6,0.92)';
    ctx.fillStyle = color;
    ctx.beginPath();
    const rad = boxH / 2;
    ctx.moveTo(cx - boxW / 2 + rad, cy - boxH / 2);
    ctx.lineTo(cx + boxW / 2 - rad, cy - boxH / 2);
    ctx.arcTo(cx + boxW / 2, cy - boxH / 2, cx + boxW / 2, cy, rad);
    ctx.lineTo(cx + boxW / 2, cy + boxH / 2 - rad);
    ctx.arcTo(cx + boxW / 2, cy + boxH / 2, cx + boxW / 2 - rad, cy + boxH / 2, rad);
    ctx.lineTo(cx - boxW / 2 + rad, cy + boxH / 2);
    ctx.arcTo(cx - boxW / 2, cy + boxH / 2, cx - boxW / 2, cy, rad);
    ctx.lineTo(cx - boxW / 2, cy - boxH / 2 + rad);
    ctx.arcTo(cx - boxW / 2, cy - boxH / 2, cx - boxW / 2 + rad, cy - boxH / 2, rad);
    ctx.closePath();
    ctx.fill();

    // Texto
    ctx.fillStyle = 'white';
    ctx.fillText(text, cx, cy);
    ctx.restore();
  }

  // --------------------- Grabación ---------------------
  const startRecording = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    chunksRef.current = [];
    const stream = canvas.captureStream(30);
    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType, videoBitsPerSecond: 2_500_000 } : undefined);
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      isRecordingRef.current = false;
      const blob = new Blob(chunksRef.current, { type: mimeType || 'video/webm' });
      const url = URL.createObjectURL(blob);
      setRecordedUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
      setRecordedMime(mimeType || 'video/webm');
      setIsRecording(false);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      // Correr análisis biomecánico sobre los landmarks capturados
      try { runAnalysisRef.current(); } catch (e) { console.error('Analysis error:', e); }
    };
    // Reiniciar buffer y resultados previos
    landmarksBufferRef.current = [];
    setAnalysisResult(null);
    setComparisonResult(null);
    recorder.start(250);
    recorderRef.current = recorder;
    startTsRef.current = performance.now();
    setElapsed(0);
    setIsRecording(true);
    isRecordingRef.current = true;
    timerRef.current = setInterval(() => setElapsed(performance.now() - startTsRef.current), 250);
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  };

  const downloadRecording = () => {
    if (!recordedUrl) return;
    const ext = recordedMime.includes('mp4') ? 'mp4' : 'webm';
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const a = document.createElement('a');
    a.href = recordedUrl;
    a.download = `pose-${ts}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const discardRecording = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedUrl(null);
    setRecordedMime('');
  };

  // --------------------- Cámara lenta ---------------------
  const downloadSlowMotion = async (speed) => {
    if (!recordedUrl || slowMoProcessing) return;
    setSlowMoProcessing(speed);
    setSlowMoProgress(0);
    try {
      const srcVideo = document.createElement('video');
      srcVideo.src = recordedUrl;
      srcVideo.muted = true;
      srcVideo.playsInline = true;
      await new Promise((res, rej) => {
        srcVideo.onloadedmetadata = res;
        srcVideo.onerror = () => rej(new Error('No se pudo leer el video'));
      });
      const w = srcVideo.videoWidth;
      const h = srcVideo.videoHeight;
      const duration = srcVideo.duration || 0;
      srcVideo.playbackRate = speed; // 0.5 = mitad de velocidad

      const off = document.createElement('canvas');
      off.width = w; off.height = h;
      const octx = off.getContext('2d');

      const outStream = off.captureStream(30);
      const mt = pickMimeType();
      const chunks = [];
      const rec = new MediaRecorder(outStream, mt ? { mimeType: mt, videoBitsPerSecond: 3_500_000 } : undefined);
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      const recStopped = new Promise((res) => { rec.onstop = res; });

      let raf = null;
      const loop = () => {
        if (srcVideo.ended || srcVideo.paused) return;
        octx.drawImage(srcVideo, 0, 0, w, h);
        if (duration > 0) setSlowMoProgress(Math.min(100, Math.round((srcVideo.currentTime / duration) * 100)));
        raf = requestAnimationFrame(loop);
      };

      rec.start(250);
      await srcVideo.play();
      loop();
      await new Promise((res) => { srcVideo.onended = res; });
      if (raf) cancelAnimationFrame(raf);
      rec.stop();
      await recStopped;

      const blob = new Blob(chunks, { type: mt || 'video/webm' });
      const url = URL.createObjectURL(blob);
      const ext = (mt || '').includes('mp4') ? 'mp4' : 'webm';
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pose-slow${speed}x-${ts}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error('Slow-mo error:', e);
      alert('No se pudo generar la cámara lenta: ' + (e.message || e));
    } finally {
      setSlowMoProcessing(null);
      setSlowMoProgress(0);
    }
  };

  const flipCamera = () => {
    const next = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(next);
    setLoadingMsg('Cambiando cámara...');
    setLoading(true);
    startCamera(next);
  };

  // --------------------- Análisis post-grabación ---------------------
  // Mantenemos la función "viva" vía ref para que el recorder.onstop siempre
  // use los valores más recientes de mode/exercise/referenceData.
  useEffect(() => {
    runAnalysisRef.current = () => {
      const timeline = landmarksBufferRef.current;
      if (!timeline || timeline.length < 3) return;

      if (mode === 'basic' && selectedExerciseId) {
        const exercise = getExercise(selectedExerciseId);
        if (!exercise) return;
        const result = evaluate(exercise, timeline);
        setAnalysisResult(result);
      } else if (mode === 'execution' && referenceData) {
        const gymMetrics = timeline
          .map(f => ({ t: f.t, ...computeMetrics(f.lm) }));
        const duration = timeline[timeline.length - 1].t;
        const cmp = compareSeries(
          referenceData.metrics, referenceData.duration,
          gymMetrics, duration
        );
        setComparisonResult(cmp);
      }
    };
  }, [mode, selectedExerciseId, referenceData]);

  // --------------------- Upload video de referencia ---------------------
  const handleReferenceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!poseRef.current) { alert('El modelo aún no está listo. Espera unos segundos.'); return; }
    const url = URL.createObjectURL(file);
    if (referenceVideoUrl) URL.revokeObjectURL(referenceVideoUrl);
    setReferenceVideoUrl(url);
    setReferenceData(null);
    setComparisonResult(null);
    setReferenceProcessing(true);
    setReferenceProgress(0);
    processingRefVideoRef.current = true;
    try {
      const smootherFactory = () => createLandmarkSmoother(33);
      const data = await processVideo(
        url, poseRef.current, smootherFactory,
        (p) => setReferenceProgress(Math.round(p * 100)),
        12
      );
      if (data.timeline.length < 3) throw new Error('No se detectó una persona claramente en el video.');
      setReferenceData(data);
    } catch (err) {
      console.error('Reference video error:', err);
      alert('No se pudo procesar el video: ' + (err.message || err));
      URL.revokeObjectURL(url);
      setReferenceVideoUrl(null);
    } finally {
      setReferenceProcessing(false);
      processingRefVideoRef.current = false;
      lastVideoTimeRef.current = -1; // fuerza reanudar detección
    }
  };

  const clearReference = () => {
    if (referenceVideoUrl) URL.revokeObjectURL(referenceVideoUrl);
    setReferenceVideoUrl(null);
    setReferenceData(null);
    setComparisonResult(null);
  };

  const changeMode = () => {
    setMode(null);
    setAnalysisResult(null);
    setComparisonResult(null);
    clearReference();
  };

  // --------------------- UI ---------------------
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 30, color: '#dc2626' }}>
        <AlertTriangle size={40} />
        <p style={{ marginTop: 12, fontWeight: 600 }}>{error}</p>
      </div>
    );
  }

  const selectedExercise = selectedExerciseId ? getExercise(selectedExerciseId) : null;
  const canRecord = !loading && (
    (mode === 'basic' && !!selectedExerciseId) ||
    (mode === 'execution' && !!referenceData)
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ============ Selector de modo (pantalla inicial) ============ */}
      {!mode && (
        <div style={{
          background: 'linear-gradient(135deg, #faf5ff 0%, #eff6ff 100%)',
          border: '1px solid #e9d5ff', borderRadius: 16, padding: 20,
        }}>
          <h3 style={{ margin: '0 0 6px', fontSize: '1.05rem', color: '#18181b' }}>
            ¿Qué tipo de análisis quieres hacer?
          </h3>
          <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: '#71717a' }}>
            Elige el modo antes de empezar a grabar. Todo se procesa en tu dispositivo.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            <button
              onClick={() => setMode('basic')}
              style={{
                textAlign: 'left', background: 'white', border: '2px solid #e4e4e7',
                borderRadius: 14, padding: 18, cursor: 'pointer',
                display: 'flex', flexDirection: 'column', gap: 8,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(139,92,246,0.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e4e4e7'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: '#f3e8ff', color: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Target size={22} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: '#18181b' }}>Evaluación básica</div>
                  <div style={{ fontSize: '0.78rem', color: '#71717a' }}>Basada en teoría (USAG)</div>
                </div>
              </div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#52525b', lineHeight: 1.5 }}>
                Elige un ejercicio del catálogo. El sistema evalúa ángulos, simetría y alineación contra los estándares técnicos y te devuelve alertas con drills sugeridos.
              </p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                <span style={{ fontSize: '0.7rem', background: '#faf5ff', color: '#7c3aed', padding: '3px 8px', borderRadius: 100, fontWeight: 600 }}>{EXERCISE_LIST.length} ejercicios</span>
                <span style={{ fontSize: '0.7rem', background: '#ecfdf5', color: '#059669', padding: '3px 8px', borderRadius: 100, fontWeight: 600 }}>Niveles 1-5</span>
              </div>
            </button>

            <button
              onClick={() => setMode('execution')}
              style={{
                textAlign: 'left', background: 'white', border: '2px solid #e4e4e7',
                borderRadius: 14, padding: 18, cursor: 'pointer',
                display: 'flex', flexDirection: 'column', gap: 8,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#0ea5e9'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(14,165,233,0.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e4e4e7'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: '#e0f2fe', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Sparkles size={22} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: '#18181b' }}>Análisis de ejecución</div>
                  <div style={{ fontSize: '0.78rem', color: '#71717a' }}>Compara con video modelo</div>
                </div>
              </div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#52525b', lineHeight: 1.5 }}>
                Sube un video de referencia (tú, una gimnasta modelo o un tutorial). El sistema extrae los ángulos clave y los compara con la ejecución de tu atleta.
              </p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                <span style={{ fontSize: '0.7rem', background: '#e0f2fe', color: '#0369a1', padding: '3px 8px', borderRadius: 100, fontWeight: 600 }}>7 métricas clave</span>
                <span style={{ fontSize: '0.7rem', background: '#f0fdf4', color: '#15803d', padding: '3px 8px', borderRadius: 100, fontWeight: 600 }}>Similitud %</span>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* ============ Header modo activo ============ */}
      {mode === 'basic' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 12, padding: 12, flexWrap: 'wrap' }}>
          <button onClick={changeMode} title="Cambiar modo" style={{ background: 'white', border: '1px solid #e9d5ff', borderRadius: 8, padding: '6px 10px', display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.82rem', color: '#7c3aed', fontWeight: 600 }}>
            <ChevronLeft size={14} /> Modo
          </button>
          <Target size={18} style={{ color: '#7c3aed' }} />
          <strong style={{ color: '#581c87', fontSize: '0.9rem' }}>Evaluación básica:</strong>
          <select
            value={selectedExerciseId || ''}
            onChange={e => setSelectedExerciseId(e.target.value)}
            disabled={isRecording}
            style={{ flex: 1, minWidth: 200, border: '1px solid #d8b4fe', borderRadius: 8, padding: '8px 10px', fontSize: '0.88rem', background: 'white', color: '#27272a', cursor: isRecording ? 'not-allowed' : 'pointer' }}
          >
            {EXERCISE_LIST.map(ex => (
              <option key={ex.id} value={ex.id}>N{ex.level} · {ex.name}</option>
            ))}
          </select>
        </div>
      )}

      {mode === 'execution' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 12, padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={changeMode} title="Cambiar modo" style={{ background: 'white', border: '1px solid #bae6fd', borderRadius: 8, padding: '6px 10px', display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.82rem', color: '#0369a1', fontWeight: 600 }}>
              <ChevronLeft size={14} /> Modo
            </button>
            <Sparkles size={18} style={{ color: '#0284c7' }} />
            <strong style={{ color: '#0c4a6e', fontSize: '0.9rem' }}>Análisis de ejecución</strong>
            {referenceData && (
              <span style={{ fontSize: '0.72rem', background: '#d1fae5', color: '#065f46', padding: '3px 8px', borderRadius: 100, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <CheckCircle2 size={12} /> Modelo listo ({referenceData.timeline.length} frames)
              </span>
            )}
          </div>

          {!referenceVideoUrl && !referenceProcessing && (
            <label style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              background: 'white', border: '2px dashed #7dd3fc', borderRadius: 10,
              padding: 16, cursor: 'pointer', fontSize: '0.88rem', color: '#0369a1', fontWeight: 600,
            }}>
              <Upload size={18} />
              <span>Subir video modelo de referencia</span>
              <input type="file" accept="video/*" onChange={handleReferenceUpload} hidden />
            </label>
          )}

          {referenceProcessing && (
            <div style={{ background: 'white', borderRadius: 10, padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <Loader size={16} style={{ animation: 'spin 1s linear infinite', color: '#0284c7' }} />
                <strong style={{ fontSize: '0.85rem', color: '#0c4a6e' }}>Procesando video... {referenceProgress}%</strong>
              </div>
              <div style={{ height: 6, background: '#e0f2fe', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ width: `${referenceProgress}%`, height: '100%', background: 'linear-gradient(90deg, #0ea5e9, #0284c7)', transition: 'width 0.3s' }} />
              </div>
            </div>
          )}

          {referenceVideoUrl && !referenceProcessing && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', background: 'white', borderRadius: 10, padding: 10 }}>
              <video src={referenceVideoUrl} controls style={{ width: 140, height: 80, objectFit: 'cover', borderRadius: 8, background: '#000' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0c4a6e', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <FileVideo size={14} /> Video modelo
                </div>
                <div style={{ fontSize: '0.75rem', color: '#0369a1' }}>
                  {referenceData ? `${(referenceData.duration / 1000).toFixed(1)}s · ${referenceData.timeline.length} muestras` : 'Procesado'}
                </div>
              </div>
              <button onClick={clearReference} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Quitar video">
                <XIcon size={18} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Stage */}
      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: videoAspect < 1 ? `calc(85vh * ${videoAspect})` : '100%',
        margin: '0 auto',
        background: '#000',
        borderRadius: 12,
        overflow: 'hidden',
        aspectRatio: String(videoAspect),
        maxHeight: '85vh',
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
      }}>
        {/* Hidden raw video (fuente) */}
        <video ref={videoRef} playsInline muted style={{ display: 'none' }} />
        {/* Canvas donde se compone video + pose */}
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />

        {/* Loading overlay */}
        {loading && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.8)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            color: 'white', gap: 12,
          }}>
            <Loader size={32} className="cch-spinner-icon" style={{ animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: '0.9rem' }}>{loadingMsg}</span>
          </div>
        )}

        {/* Recording badge */}
        {isRecording && (
          <div style={{
            position: 'absolute', top: 12, left: 12,
            background: 'rgba(239, 68, 68, 0.95)',
            color: 'white', padding: '6px 12px',
            borderRadius: 20, fontSize: '0.8rem', fontWeight: 700,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              background: 'white', animation: 'pulse 1s infinite',
            }} />
            REC {fmtTime(elapsed)}
          </div>
        )}

        {/* FPS */}
        {!loading && (
          <div style={{
            position: 'absolute', top: 12, right: 12,
            background: 'rgba(0,0,0,0.55)', color: '#a7f3d0',
            padding: '4px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600,
            fontFamily: 'monospace',
          }}>
            {fps} FPS
          </div>
        )}
      </div>

      {/* Controles */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
        {!isRecording ? (
          <button
            onClick={startRecording}
            disabled={!canRecord}
            title={
              loading ? 'Esperando cámara' :
              !mode ? 'Primero elige un modo de análisis' :
              mode === 'execution' && !referenceData ? 'Sube un video modelo primero' :
              'Comenzar grabación'
            }
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 22px', borderRadius: 100, border: 'none',
              background: !canRecord ? '#d4d4d8' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white', fontWeight: 700, fontSize: '0.95rem',
              cursor: !canRecord ? 'not-allowed' : 'pointer',
              boxShadow: !canRecord ? 'none' : '0 4px 12px rgba(220,38,38,0.3)',
            }}
          >
            <Circle size={16} fill="white" /> Grabar
          </button>
        ) : (
          <button
            onClick={stopRecording}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 22px', borderRadius: 100, border: 'none',
              background: '#18181b', color: 'white',
              fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            }}
          >
            <Square size={14} fill="white" /> Detener
          </button>
        )}

        <button
          onClick={flipCamera}
          disabled={loading || isRecording}
          title="Cambiar cámara"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '12px 18px', borderRadius: 100,
            border: '1px solid #e4e4e7', background: 'white',
            color: '#27272a', fontWeight: 600, fontSize: '0.9rem',
            cursor: (loading || isRecording) ? 'not-allowed' : 'pointer',
            opacity: (loading || isRecording) ? 0.5 : 1,
          }}
        >
          <RotateCcw size={16} /> Girar
        </button>

        <button
          onClick={() => setShowLandmarks(v => !v)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '12px 18px', borderRadius: 100,
            border: '1px solid #e4e4e7',
            background: showLandmarks ? '#ede9fe' : 'white',
            color: showLandmarks ? '#6d28d9' : '#27272a',
            fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
          }}
        >
          {showLandmarks ? 'Ocultar esqueleto' : 'Mostrar esqueleto'}
        </button>
      </div>

      {/* Preview + descarga post grabación */}
      {recordedUrl && (
        <div style={{
          background: '#fafafa', border: '1px solid #e4e4e7',
          borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#18181b' }}>Grabación lista</h4>

          {/* ========= Resultado: Evaluación básica ========= */}
          {mode === 'basic' && analysisResult && (
            <div style={{ background: 'white', border: '1px solid #e4e4e7', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: `conic-gradient(#10b981 ${analysisResult.summary.score * 3.6}deg, #f4f4f5 0deg)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '1rem', color: '#18181b' }}>
                    {Math.round(analysisResult.summary.score)}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontSize: '0.82rem', color: '#71717a', fontWeight: 600 }}>{selectedExercise?.name}</div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: '#18181b' }}>
                    {analysisResult.summary.alertCount === 0
                      ? '¡Ejecución limpia!'
                      : `${analysisResult.summary.alertCount} ${analysisResult.summary.alertCount === 1 ? 'punto' : 'puntos'} a mejorar`}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#71717a' }}>
                    {analysisResult.summary.criticalCount > 0 && <span style={{ color: '#dc2626', fontWeight: 600 }}>{analysisResult.summary.criticalCount} crítico(s) · </span>}
                    {analysisResult.summary.warningCount > 0 && <span style={{ color: '#d97706', fontWeight: 600 }}>{analysisResult.summary.warningCount} advertencia(s) · </span>}
                    {analysisResult.summary.totalFrames} frames analizados
                  </div>
                </div>
              </div>

              {analysisResult.alerts.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {analysisResult.alerts.map(a => {
                    const color = a.level === 'critical' ? '#dc2626' : a.level === 'warning' ? '#d97706' : '#0ea5e9';
                    const bg = a.level === 'critical' ? '#fef2f2' : a.level === 'warning' ? '#fffbeb' : '#eff6ff';
                    const jumpToMoment = () => {
                      const v = recordedVideoRef.current;
                      if (!v) return;
                      v.currentTime = Math.max(0, a.worst.t / 1000 - 0.2);
                      v.play().catch(() => {});
                      v.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    };
                    return (
                      <div
                        key={a.id}
                        onClick={jumpToMoment}
                        title="Ir al momento en el video"
                        style={{ background: bg, border: `1px solid ${color}33`, borderRadius: 10, padding: 12, cursor: 'pointer', transition: 'transform 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(3px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateX(0)'; }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <AlertTriangle size={14} style={{ color }} />
                          <strong style={{ fontSize: '0.88rem', color: '#18181b' }}>{a.name}</strong>
                          <span style={{ fontSize: '0.7rem', background: color, color: 'white', padding: '2px 8px', borderRadius: 100, fontWeight: 700, marginLeft: 'auto' }}>
                            {Math.round(a.outRatio * 100)}% de los frames
                          </span>
                        </div>
                        <p style={{ margin: '0 0 6px', fontSize: '0.83rem', color: '#3f3f46' }}>{a.message}</p>
                        <div style={{ fontSize: '0.72rem', color: '#71717a' }}>
                          Peor momento: <strong>{(a.worst.t / 1000).toFixed(1)}s</strong>
                          {a.type === 'angulo' && ` — ángulo ${a.worst.value.toFixed(0)}°`}
                          {a.type === 'apertura_piernas' && ` — apertura ${a.worst.value.toFixed(0)}°`}
                          {a.type === 'simetria_angulo' && ` — diferencia ${a.worst.value.toFixed(1)}°`}
                          {a.type === 'linealidad' && ` — desviación ${(a.worst.value * 100).toFixed(1)}%`}
                          {a.type === 'distancia_norm' && ` — separación ${(a.worst.value * 100).toFixed(1)}%`}
                        </div>
                        {a.drill && (
                          <div style={{ marginTop: 6, fontSize: '0.75rem', color: color, fontWeight: 600 }}>
                            💡 {a.drill}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {analysisResult.alerts.length === 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#ecfdf5', borderRadius: 10, padding: 12, color: '#065f46' }}>
                  <CheckCircle2 size={20} />
                  <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>Sin observaciones. La ejecución cumple los parámetros técnicos evaluados.</span>
                </div>
              )}
            </div>
          )}

          {/* ========= Resultado: Análisis de ejecución ========= */}
          {mode === 'execution' && comparisonResult && (
            <div style={{ background: 'white', border: '1px solid #e4e4e7', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: `conic-gradient(#0ea5e9 ${comparisonResult.similarity * 3.6}deg, #f4f4f5 0deg)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.95rem', color: '#18181b' }}>
                    {Math.round(comparisonResult.similarity)}%
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontSize: '0.82rem', color: '#71717a', fontWeight: 600 }}>Similitud técnica</div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: '#18181b' }}>
                    {comparisonResult.similarity >= 85 ? 'Ejecución muy cercana al modelo' :
                     comparisonResult.similarity >= 70 ? 'Buena base, hay ajustes por hacer' :
                     'Diferencias significativas con el modelo'}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#71717a' }}>
                    Error angular promedio: {comparisonResult.avgRmse.toFixed(1)}°
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {Object.entries(comparisonResult.perMetric)
                  .filter(([, v]) => v.rmse != null)
                  .sort(([, a], [, b]) => (b.rmse || 0) - (a.rmse || 0))
                  .slice(0, 7)
                  .map(([key, v]) => {
                    const isBodyLine = key === 'body_line';
                    const maxExpected = isBodyLine ? 80 : 30;
                    const severity = Math.min(1, (v.rmse || 0) / maxExpected);
                    const color = severity < 0.33 ? '#10b981' : severity < 0.66 ? '#f59e0b' : '#dc2626';
                    return (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ fontSize: '0.8rem', color: '#18181b', fontWeight: 600, minWidth: 160 }}>{v.name}</div>
                        <div style={{ flex: 1, height: 8, background: '#f4f4f5', borderRadius: 10, overflow: 'hidden' }}>
                          <div style={{ width: `${severity * 100}%`, height: '100%', background: color, transition: 'width 0.4s' }} />
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#71717a', fontFamily: 'monospace', minWidth: 60, textAlign: 'right' }}>
                          {isBodyLine ? `${(v.rmse || 0).toFixed(1)}` : `${(v.rmse || 0).toFixed(1)}°`}
                        </div>
                      </div>
                    );
                  })}
              </div>

              <div style={{ fontSize: '0.72rem', color: '#71717a', background: '#fafafa', padding: 8, borderRadius: 8 }}>
                Barra más corta = métrica más parecida al modelo. El error está en grados (excepto alineación, que es adimensional).
              </div>
            </div>
          )}

          <video
            ref={recordedVideoRef}
            src={recordedUrl}
            controls
            playsInline
            style={{ width: '100%', maxHeight: '50vh', borderRadius: 8, background: '#000' }}
          />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={downloadRecording}
              disabled={!!slowMoProcessing}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 18px', borderRadius: 8, border: 'none',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white', fontWeight: 700, fontSize: '0.88rem',
                cursor: slowMoProcessing ? 'not-allowed' : 'pointer',
                opacity: slowMoProcessing ? 0.6 : 1,
                boxShadow: '0 4px 12px rgba(124,58,237,0.25)',
              }}
            >
              <Download size={16} /> Descargar normal
            </button>
            <button
              onClick={() => downloadSlowMotion(0.5)}
              disabled={!!slowMoProcessing}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 18px', borderRadius: 8, border: 'none',
                background: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)',
                color: 'white', fontWeight: 700, fontSize: '0.88rem',
                cursor: slowMoProcessing ? 'not-allowed' : 'pointer',
                opacity: slowMoProcessing ? 0.6 : 1,
                boxShadow: '0 4px 12px rgba(14,165,233,0.25)',
              }}
            >
              <Download size={16} /> {slowMoProcessing === 0.5 ? `Procesando ${slowMoProgress}%` : 'Cámara lenta 0.5×'}
            </button>
            <button
              onClick={() => downloadSlowMotion(0.25)}
              disabled={!!slowMoProcessing}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 18px', borderRadius: 8, border: 'none',
                background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
                color: 'white', fontWeight: 700, fontSize: '0.88rem',
                cursor: slowMoProcessing ? 'not-allowed' : 'pointer',
                opacity: slowMoProcessing ? 0.6 : 1,
                boxShadow: '0 4px 12px rgba(20,184,166,0.25)',
              }}
            >
              <Download size={16} /> {slowMoProcessing === 0.25 ? `Procesando ${slowMoProgress}%` : 'Cámara lenta 0.25×'}
            </button>
            <button
              onClick={discardRecording}
              disabled={!!slowMoProcessing}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 18px', borderRadius: 8,
                border: '1px solid #e4e4e7', background: 'white',
                color: '#dc2626', fontWeight: 600, fontSize: '0.88rem',
                cursor: slowMoProcessing ? 'not-allowed' : 'pointer',
                opacity: slowMoProcessing ? 0.6 : 1,
              }}
            >
              <Trash2 size={16} /> Descartar
            </button>
          </div>
          <p style={{ margin: 0, fontSize: '0.75rem', color: '#71717a' }}>
            El video se guardará en la carpeta de descargas de tu dispositivo. No se envía a ningún servidor.
            La cámara lenta se re-codifica en tu dispositivo (puede tardar unos segundos).
          </p>
        </div>
      )}

      {/* Info breve */}
      <div style={{
        fontSize: '0.78rem', color: '#71717a', background: '#f8fafc',
        padding: 10, borderRadius: 8, border: '1px dashed #e2e8f0',
      }}>
        <strong style={{ color: '#475569' }}>Privacidad:</strong> el procesamiento ocurre 100% en tu dispositivo.
        Las imágenes nunca salen de tu celular/computadora.
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
      `}</style>
    </div>
  );
};

export default PoseCamera;
